import React, { useEffect, useRef, useState } from "react";
import { api } from "@/api";
import { CalendarClock } from "lucide-react";

const fmtClock = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return "";
  }
};

function StatusChip({ status }) {
  if (status === "live")
    return <span className="bg-[#E63946] text-white text-[10px] font-black uppercase px-2 py-0.5 tracking-widest whitespace-nowrap">● In Water</span>;
  if (status === "on_deck")
    return <span className="bg-[#FFD700] text-black text-[10px] font-black uppercase px-2 py-0.5 tracking-widest whitespace-nowrap">On Deck</span>;
  if (status === "done")
    return <span className="bg-black text-white text-[10px] font-black uppercase px-2 py-0.5 tracking-widest whitespace-nowrap">Done</span>;
  return <span className="brutal-2 text-gray-500 text-[10px] font-black uppercase px-2 py-0.5 tracking-widest whitespace-nowrap">Upcoming</span>;
}

export default function ScheduleItinerary() {
  const [plan, setPlan] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try {
      setPlan(await api.schedulePlan());
    } catch (e) {
      /* keep last */
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, []);

  if (!plan || !plan.total_heats) return null;

  const upcoming = plan.heats.filter((h) => h.status === "upcoming" || h.status === "scheduled");
  const nextHeat = plan.heats.find((h) => h.status === "on_deck") || upcoming[0];

  return (
    <div className="brutal p-4 mt-6" data-testid="schedule-itinerary">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock size={22} className="shrink-0" />
        <h2 className="font-display text-xl font-black uppercase tracking-tight">Today's Schedule</h2>
      </div>

      {!plan.running ? (
        <p className="font-display text-xs uppercase tracking-widest text-gray-500 mb-3" data-testid="schedule-not-started">
          {plan.start_at ? `First heat at ${fmtClock(plan.start_at)}` : "Schedule not set yet"} · {plan.heat_duration_min} min heats
        </p>
      ) : plan.paused ? (
        <p className="font-display text-xs uppercase tracking-widest text-[#E63946] mb-3" data-testid="schedule-paused">
          ⏸ Paused by organizer — heats resume shortly
        </p>
      ) : (
        <p className="font-display text-xs uppercase tracking-widest text-green-700 mb-3" data-testid="schedule-running">
          ● Running · {plan.heat_duration_min} min heats
        </p>
      )}

      <div className="brutal-2 bg-[#FFD700] p-2 mb-3">
        <p className="font-display text-xs font-black uppercase tracking-tight text-black">
          🏄 Check in with the beach marshal <strong>5 minutes before</strong> your heat &amp; grab your jersey.
        </p>
      </div>

      {nextHeat && (
        <div className="brutal-2 bg-black text-white p-3 mb-3" data-testid="schedule-next-up">
          <div className="font-display text-[10px] uppercase tracking-[0.25em] text-[#FFD700]">Up Next</div>
          <div className="font-display font-black uppercase tracking-tight text-lg leading-tight">
            {nextHeat.division_name} · {nextHeat.name}
          </div>
          <div className="font-display text-xs uppercase tracking-widest text-gray-300">
            {nextHeat.round_name}{nextHeat.scheduled_at ? ` · ${fmtClock(nextHeat.scheduled_at)}` : ""}
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1" data-testid="schedule-list">
        {plan.heats.map((h) => (
          <div
            key={h.id}
            data-testid={`schedule-heat-${h.id}`}
            className={`brutal-2 p-2 flex items-center gap-3 ${
              h.status === "live" ? "border-[#E63946] bg-red-50" : h.status === "done" ? "opacity-60 bg-gray-50" : "bg-white"
            }`}
          >
            <div className="w-16 shrink-0 text-center">
              <div className="font-score text-2xl leading-none">{fmtClock(h.scheduled_at) || "—"}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black uppercase tracking-tight truncate text-sm leading-tight">
                {h.division_name}
                {h.is_final && <span className="ml-2 bg-[#FFD700] text-black text-[9px] font-black uppercase px-1.5 py-0.5 tracking-widest">Final</span>}
              </div>
              <div className="font-display text-[11px] uppercase tracking-widest text-gray-500 truncate">
                {h.round_name} · {h.name}
                {h.riders && h.riders.length ? ` — ${h.riders.join(", ")}` : ""}
              </div>
            </div>
            <StatusChip status={h.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
